import { createFileRoute, Link } from "@tanstack/react-router";
import madsPortrait from "@/assets/mads-poulsen-portrait.png.asset.json";

const SITE_URL = "https://flaneriet.online";

export const Route = createFileRoute("/om-kuratoren")({
  head: () => ({
    meta: [
      { title: "Om kuratoren – Mads Poulsen | Flâneriet" },
      {
        name: "description",
        content:
          "Mads Poulsen – far, trommeslager og udvikler. Kurator bag Flâneriet, det stille modsvar til feedet.",
      },
      { property: "og:type", content: "profile" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Om kuratoren – Mads Poulsen" },
      {
        property: "og:description",
        content:
          "Kuratering uden et menneske er bare et bogmærke-ark. Mød manden bag Flâneriet.",
      },
      { property: "og:url", content: `${SITE_URL}/om-kuratoren` },
      { property: "og:image", content: `${SITE_URL}${madsPortrait.url}` },
      { property: "og:image:alt", content: "Mads Poulsen – kurator, Flâneriet" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Om kuratoren – Mads Poulsen" },
      {
        name: "twitter:description",
        content:
          "Far, trommeslager og udvikler. Kurator bag det stille modsvar til feedet.",
      },
      { name: "twitter:image", content: `${SITE_URL}${madsPortrait.url}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/om-kuratoren` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: "Mads Poulsen",
            jobTitle: "Kurator, Flâneriet",
            description:
              "Far, trommeslager og frontendudvikler. Forsøger at finde løsninger, hvor teknologien bidrager til, at mennesker kan være mere menneske.",
            url: `${SITE_URL}/om-kuratoren`,
            image: `${SITE_URL}${madsPortrait.url}`,
          },
        }),
      },
    ],
  }),
  component: OmKuratorenPage,
});

function OmKuratorenPage() {
  return (
    <article className="mx-auto max-w-4xl">
      <header className="pt-14 pb-8 text-center border-b border-rule">
        <p className="eyebrow">Om kuratoren</p>
        <h1 className="mt-3 font-serif text-[2.6rem] md:text-[3.4rem] leading-[1.04] text-ink">
          Mads Poulsen
        </h1>
        <p className="mt-4 meta italic max-w-xl mx-auto">
          Far, trommeslager, udvikler – og manden, der læser på dine vegne.
        </p>
      </header>

      <section className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-10 md:gap-14 py-14 items-start">
        <figure className="m-0">
          <div className="relative w-full aspect-[4/5] overflow-hidden border border-rule bg-paper-alt">
            <img
              src={madsPortrait.url}
              alt="Portræt af Mads Poulsen, kurator på Flâneriet"
              width={480}
              height={600}
              loading="eager"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                filter:
                  "grayscale(0.32) sepia(0.22) contrast(0.98) brightness(1.02)",
              }}
            />
          </div>
          <figcaption className="mt-3 font-mono text-[10.5px] tracking-[0.12em] uppercase text-muted text-center">
            Mads Poulsen · Kurator
          </figcaption>
        </figure>

        <div className="font-serif text-[1.1rem] md:text-[1.18rem] leading-[1.75] text-ink-body space-y-6">
          <p className="font-serif italic text-[1.25rem] md:text-[1.35rem] leading-[1.55] text-ink not-italic">
            <span className="italic">
              Kuratering uden et menneske er bare et bogmærke-ark. Så her er
              ansigtet bag.
            </span>
          </p>

          <p>
            Jeg hedder Mads. Jeg er far til to, trommeslager af hjertet og
            frontendudvikler i det daglige. Jeg forsøger at finde løsninger,
            hvor teknologien og AI bidrager til, at vi kan være mere
            menneske – ikke mindre.
          </p>

          <p>
            Flâneriet er det modsatte af et feed. Det er min hylde i reolen,
            åben for hvem der vil læse med. Hver dag læser, lytter og ser jeg
            på jeres vegne – og peger videre til dem, der virkelig kan skrive
            om vin, kunst, jazz, poesi, mad, cykling og levekunst. Jeg skriver
            ikke selv artiklerne. Jeg vælger dem, og forklarer <em>hvorfor</em>{" "}
            netop de er tiden værd.
          </p>

          <p>
            Der er ingen algoritmer her, ingen sporing, ingen notifikationer.
            Der er kun ro, opmærksomhed og et par ord om, hvad der er værd at
            åbne. Om det så er en søndag med kaffe og cahier'en i hånden, eller
            en tirsdag aften med et glas vin – så håber jeg, du finder noget,
            der giver dagen dybde.
          </p>

          <p className="font-serif italic text-[1.15rem] md:text-[1.25rem] leading-[1.6] text-ink-soft pt-2">
            Lidt mere menneske.
          </p>

          <div className="pt-4 flex flex-wrap gap-3 items-center">
            <a
              href="mailto:mail@madspoulsen.dk"
              className="inline-flex items-center gap-2 eyebrow border border-ink text-ink px-5 py-3 hover:bg-accent/10 hover:border-accent hover:text-accent transition-colors"
            >
              Skriv til mig
            </a>
            <Link
              to="/stoet"
              className="inline-flex items-center gap-2 eyebrow border border-accent text-accent px-5 py-3 hover:bg-accent/10 hover:text-accent-dark transition-colors"
            >
              Støt Flâneriet
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 border-t border-rule text-center">
        <p className="eyebrow">Manifest</p>
        <p className="mt-5 font-serif text-[1.05rem] md:text-[1.15rem] leading-[1.7] italic text-ink-soft max-w-xl mx-auto">
          Vil du læse hvorfor Flâneriet findes?
        </p>
        <p className="mt-3">
          <Link
            to="/om"
            className="font-serif text-accent hover:text-accent-dark underline underline-offset-4 decoration-[0.5px]"
          >
            Læs manifestet →
          </Link>
        </p>
      </section>
    </article>
  );
}
